// A tiny wrapper around fetch(), borrowed from
// https://kentcdodds.com/blog/replace-axios-with-a-simple-custom-fetch-wrapper

import { loginRequest } from "./authConfig";
import { msalInstance } from "src";
import { BrowserAuthError, InteractionRequiredAuthError } from "@azure/msal-browser";
import buildDataObjectExpressionsXml from "./buildDoExpressionsXml";

export async function client(endpoint, { body, ...customConfig } = {}) {
  const token = await acquireAccessToken();
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }
  const config = {
    method: body ? 'POST' : 'GET',
    ...customConfig,
    headers: {
      ...headers,
      ...customConfig.headers,
    },
  }
  if (body) {
    config.body = JSON.stringify(body)
  }

  let data
  try {
    const response = await window.fetch(endpoint, config)

    if (response.headers.get('Content-Type').indexOf('application/json') === 0) {

      data = await response.json();
    }
    else if (response.headers.get('Content-Type').indexOf('application/octet-stream') === 0) {
      data = response.body;
    }
    else {
      data = await response.text();
    }
    if (response.ok) {
      // Return a result object similar to Axios
      return {
        status: response.status,
        data,
        headers: response.headers,
        url: response.url,
      }
    }
    throw new Error(data?.Message ? data.Message : response.statusText)
  } catch (err) {
    return Promise.reject(err.message ? err.message : data)
  }
}

export const acquireAccessToken = async () => {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    throw Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
  }
  const response = await msalInstance.acquireTokenSilent({
    ...loginRequest,
    account: account
  }).catch(error => {
    if (error instanceof InteractionRequiredAuthError
      || error instanceof BrowserAuthError) {
      // fallback to interaction when silent call fails
      msalInstance.acquireTokenRedirect(loginRequest)
    }
  });
  return response.accessToken;
};

client.getOdata = function (objectName, customConfig = {}) {
  return client(process.env.REACT_APP_BASE_API + 'OData/' + objectName, { ...customConfig, method: 'GET' })
}

client.putOdata = function (objectName, body, customConfig = {}) {
  return client(process.env.REACT_APP_BASE_API + 'OData/' + objectName, { ...customConfig, body, method: 'PUT' })
}
client.postOdata = function (objectName, body, customConfig = {}) {
  return client(process.env.REACT_APP_BASE_API + 'OData/' + objectName, { ...customConfig, body, method: 'POST' })
}

client.getWebservice = function (serviceName, customConfig = {}) {
  return client.get(process.env.REACT_APP_BASE_API + 'Webservice/' + serviceName, customConfig);
}

client.get = function (endpoint, customConfig = {}) {
  return client(endpoint, { ...customConfig, method: 'GET' })
}

client.post = function (endpoint, body, customConfig = {}) {
  return client(endpoint, { ...customConfig, body })
}

client.postWebservice = function (serviceName, body, customConfig = {}) {
  return client.post(process.env.REACT_APP_BASE_API + 'Webservice/' + serviceName, body, customConfig);
}

client.getPagingData = function (dataType, viewId, page, pageSize, sortColumn, sortDirection, searchString, filters, customConfig, dataTypeArgs) {
  let datetime = new Date();
  let timestamp = datetime.getTime();
  let search = null;

  if (searchString) {
    search = searchString;
  }

  let dtArgs = {
    viewId: viewId,
    countRows: true,
    readOnlyMode: false,
    pageQueryString: '/dataobjlst.aspx?VIEW=' + viewId,
  };

  if (dataTypeArgs) {
    dtArgs = dataTypeArgs;
  }

  return client.postWebservice('JQGridPopulationWebService.asmx/GetPagingData',
    {
      dataType: dataType,
      dataTypeArgs: dtArgs,
      nd: timestamp,
      page: page,
      rows: pageSize,
      sidx: sortColumn,
      sord: sortDirection,
      _search: search !== undefined && search !== null && search.length > 0,
      filters: filters ?? null,
      searchOper: null,
      searchString: search,
      searchField: null
    },
    customConfig
  )
}

/**
 * Wrapper for JqGridPopulationWebservice that can be passed a params object in the form of GetPagingDataProps interface
 * @param {GetPagingDataProps} params 
 */
client.getPagingDataWithObject = function (params) {
  return client.getPagingData(
    params.dataType,
    params.viewId,
    params.page,
    params.pageSize,
    params.sortColumn,
    params.sortDirection,
    params.search,
    params.filters,
    params.customConfig,
    params.dataTypeArgs
  ) 
}

client.uiWsGetDataObjects = function (params) {
  let expressions = [
  ];

  if (params.expressions) {
    expressions = params.expressions;
  } else if (params.dataObjectType) {
    expressions.push(
      {
        property: 'Type',
        operator: 'Equals',
        value: params.dataObjectType
      })
  }

  let options = {
    ids: params.ids ?? null,
    expressions: buildDataObjectExpressionsXml({
      expressions: expressions
    }),
    pageContext: params.pageContext ?? "",
    path: params.path ?? null,
    populateHasCreateRight: params.populateHasCreateRight ?? null,
    properties: params.properties ?? null,
    skipNonExisting: params.skipNonExisting ?? null,
    sorting: params.sorting ?? null,
    viewId: params.viewId ?? null,
  };

  return client.postWebservice('UIWebService.asmx/GetDataObjects',
    options)
}

client.download = async function (endpoint, progressUpdate = (progress) => { }, customConfig = {}) {
  const response = await client(endpoint, { ...customConfig, method: 'GET' })
  const reader = response.data.getReader();
  // Step 2: get total length
  const contentLength = +response.headers.get('Content-Length');
  // Step 3: read the data
  let receivedLength = 0; // received that many bytes at the moment
  let chunks = []; // array of received binary chunks (comprises the body)
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    chunks.push(value);
    receivedLength += value.length;
    let progress = (receivedLength * 100) / contentLength
    if (progressUpdate && progress) { progressUpdate(progress); }
  }
  return new Blob(chunks);
}

client.getAllViews = async () => {
  let datetime = new Date();
  let timestamp = datetime.getTime();
  return client.postWebservice('JQGridPopulationWebService.asmx/GetPagingData', {
    dataType: 'Views',
    dataTypeArgs: {
      OwnerShipType: 'Both',
    },
    nd: timestamp,
    page: 1,
    rows: 500,
    sidx: 'name',
    sord: 'asc',
    _search: false,
    filters: null,
    searchOper: null,
    searchString: null,
    searchField: null
  })
}

client.getPropertyIdBySysName = function (systemName) {
    // Überprüfen, ob systemName ein String ist
    if (typeof systemName !== 'string') {
        throw new TypeError('Parameter "systemName" muss ein String sein');
    }

    return client.postWebservice("PropertyWebService.asmx/GetPropertyIdBySysName", { systemName: systemName });
}